"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContextMenuPane = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const use_t_1 = require("use-t");
const ContextSep_1 = require("../ContextSep");
const ContextPane_1 = require("../ContextPane");
const context_1 = require("./context");
const ContextMenuHeader_1 = require("./ContextMenuHeader");
const ContextMenuSearch_1 = require("./ContextMenuSearch");
const useBehaviorSubject_1 = require("../../../hooks/useBehaviorSubject");
const OpenPanelState_1 = require("./OpenPanelState");
const ExpandSection_1 = require("./ExpandSection");
const ContextHeader_1 = require("../ContextHeader");
const Breadcrumbs_1 = require("../../../3-list-item/Breadcrumbs");
const util_1 = require("./util");
const Scrollbox_1 = require("../../Scrollbox");
const popup_1 = require("../../../utils/popup");
const ContextMenuToolbarRow_1 = require("./ContextMenuToolbarRow");
const ContextMenuItem_1 = require("./ContextMenuItem");
const ContextMenuHeading_1 = require("./ContextMenuHeading");
const ContextPaneHeaderSep_1 = require("../ContextPaneHeaderSep");
const MoveToViewport_1 = require("../../../utils/popup/MoveToViewport");
const ContextMenuPane = (props) => {
    const { menu, path = [], depth = 0, inset, pane, showSearch, header, onEsc } = props;
    const [t] = (0, use_t_1.useT)();
    const state = (0, context_1.useContextMenu)();
    const search = (0, useBehaviorSubject_1.useBehaviorSubject)(state.search$);
    // biome-ignore lint/correctness/useExhaustiveDependencies: depth is only consulted at initial state creation; should not recreate the panel state
    const openPanel = React.useMemo(() => new OpenPanelState_1.OpenPanelState({ armed: depth > 0 }), []);
    React.useEffect(openPanel.start, []);
    const selected = (0, useBehaviorSubject_1.useBehaviorSubject)(openPanel.selected$);
    const anchor = (0, popup_1.useAnchorPoint)();
    const containerRef = React.useRef(null);
    const doShowSearch = showSearch && !depth && !path.length;
    // Auto-focus first item on mount.
    React.useEffect(() => {
        if (depth > 0) {
            // Popup panes are inside MoveToViewport which starts visibility:hidden
            // and reveals via requestAnimationFrame. Delay focus to run after that.
            let inner;
            const outer = requestAnimationFrame(() => {
                inner = requestAnimationFrame(() => {
                    state.focusFirstItem(containerRef.current, false, depth);
                });
            });
            return () => {
                cancelAnimationFrame(outer);
                cancelAnimationFrame(inner);
            };
        }
        state.focusFirstItem(containerRef.current, doShowSearch, depth);
        return undefined;
    }, [depth, doShowSearch, state.focusFirstItem]); // eslint-disable-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (search && openPanel.selected$.getValue())
            openPanel.deselect();
    }, [search, openPanel]);
    if (menu.pane) {
        return React.createElement(React.Fragment, null, menu.pane());
    }
    const children = menu.children;
    const hasRaw = !!menu.raw;
    const hasPanel = !!menu.panel;
    if (!children && !hasRaw && !hasPanel)
        return null;
    const length = children?.length ?? 0;
    const nodes = [];
    const bigIcons = (0, util_1.bigIconsState)(nodes, menu);
    const renderItem = (item) => {
        if (!bigIcons.flushed()) {
            if (item.iconBig) {
                bigIcons.push(item);
                return;
            }
            else
                bigIcons.flush();
        }
        const id = item.id ?? item.name;
        return (React.createElement(ContextMenuItem_1.ContextMenuItem, { key: id, item: item, path: [...path, menu], parent: menu, open: selected === id, openPanel: openPanel, renderPane: () => (React.createElement(MoveToViewport_1.MoveToViewport, null,
                React.createElement(exports.ContextMenuPane, { ...props, header: void 0, path: [...path, menu], depth: depth + 1, menu: item, onEsc: () => openPanel.deselect() }))) }));
    };
    for (let i = 0; i < length; i++) {
        const child = children[i];
        if (child.sep || child.sepBefore) {
            bigIcons.flush();
            const key = child.id ?? child.name;
            nodes.push((0, util_1.line)(key));
            if (!child.sepBefore)
                continue;
        }
        if (child.heading) {
            bigIcons.flush();
            nodes.push(React.createElement(ContextMenuHeading_1.ContextMenuHeading, { key: (child.id ?? child.name) + '-heading', item: child }));
            continue;
        }
        const subChildren = child.children;
        const expand = child.expand;
        if (expand && !!subChildren && !!subChildren.length) {
            bigIcons.flush();
            const id = (child.id ?? child.name) + '-title';
            nodes.push(React.createElement(ExpandSection_1.ExpandSection, { key: id, path: path, menu: menu, child: child, open: id === selected, renderItem: renderItem, renderPane: (item) => (React.createElement(MoveToViewport_1.MoveToViewport, null,
                    React.createElement(exports.ContextMenuPane, { ...props, path: [...path, menu], depth: depth + 1, menu: item, onEsc: () => openPanel.deselect(), header: React.createElement(React.Fragment, null,
                            React.createElement(ContextHeader_1.ContextHeader, { compact: true },
                                React.createElement(Breadcrumbs_1.Breadcrumb, { compact: true }, t(child.name))),
                            React.createElement(ContextPaneHeaderSep_1.ContextPaneHeaderSep, null)) }))), onTitleMouseEnter: () => openPanel.onMouseMove(id), onTitleMouseMove: () => openPanel.onMouseMove(id), onTitleMouseLeave: openPanel.onMouseLeave }));
        }
        else {
            if (typeof child.expandChild === 'number') {
                const item = child.children?.[child.expandChild];
                const id = child.id ?? child.name;
                if (item)
                    nodes.push(React.createElement(ContextMenuToolbarRow_1.ContextMenuToolbarRow, { key: id, path: [...path, menu, child, item], depth: depth + 1, openPanel: openPanel }));
            }
            else {
                nodes.push(renderItem(child));
            }
        }
    }
    bigIcons.flush(false);
    const searchHeight = doShowSearch ? 45 /* HEIGHT.SEARCH */ : 0;
    const doShowHeader = !depth && !!path.length;
    const headerHeight = doShowHeader || !!header ? 40 /* HEIGHT.HEADER */ : 0;
    const minWidth = menu.minWidth ?? state.minWidth ?? 220;
    const paneStyle = pane?.style ?? {};
    paneStyle.minWidth = minWidth;
    if (menu.maxWidth !== undefined) {
        paneStyle.maxWidth = menu.maxWidth;
        paneStyle.width = menu.maxWidth;
    }
    return (React.createElement(ContextPane_1.ContextPane, { ...pane, ref: containerRef, role: "menu", "aria-label": menu.name, onKeyDown: (e) => state.handleKeyDown(e, containerRef.current, openPanel, depth, onEsc), onBlur: (e) => {
            // In panel mode the body is custom content (e.g. inline ArgsPane)
            // whose controls re-render on every interaction, momentarily moving
            // focus to <body>. The focus-out close would dismiss the menu on
            // those internal blurs. The popup wrapper still handles real
            // outside clicks, so suppressing this path is safe.
            if (hasPanel)
                return;
            state.handleFocusOut(e, containerRef.current, depth, onEsc);
        }, style: paneStyle },
        !doShowHeader && header,
        (!search || !showSearch) && (React.createElement(React.Fragment, null,
            doShowHeader && React.createElement(ContextMenuHeader_1.ContextMenuHeader, null),
            React.createElement(ContextSep_1.ContextSep, null),
            React.createElement(Scrollbox_1.Scrollbox, { style: {
                    maxHeight: (anchor?.maxHeight() ?? window.innerHeight) -
                        searchHeight -
                        headerHeight -
                        7 /* HEIGHT.SEPARATOR */ -
                        7 /* HEIGHT.SEPARATOR */,
                } }, hasPanel ? menu.panel() : hasRaw ? menu.raw() : nodes))),
        React.createElement(ContextSep_1.ContextSep, null),
        doShowSearch && (React.createElement(ContextMenuSearch_1.ContextMenuSearch, { inset: inset, searchPlaceholder: props.searchPlaceholder, ContextMenuPane: exports.ContextMenuPane }))));
};
exports.ContextMenuPane = ContextMenuPane;
